import React from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import {
  BarChart3,
  Bell,
  Boxes,
  ChartNoAxesCombined,
  Coins,
  Database,
  Gift,
  LayoutDashboard,
  Mail,
  Package,
  Settings,
  ShoppingBag,
  Users,
} from "lucide-react";
import { media } from "../../styles/responsive";

/* DynamicSidebar 와 같은 규칙이다 — 넓으면 본문 옆 한 칸, 좁으면 서랍.
 * 예전에는 여기도 absolute 로 떠 있어서 좁은 화면에서 관리자 표 위에
 * 그대로 얹혔다. */
const AdminSidebar = styled.aside`
  flex: 0 0 260px;
  width: 260px;
  align-self: flex-start;
  position: sticky;
  top: var(--market-header-h);
  max-height: calc(100vh - var(--market-header-h));
  padding: 24px 0;
  background: #111827;
  border-right: 1px solid #243244;
  overflow-y: auto;
  overscroll-behavior: contain;

  ${media.lg} {
    position: fixed;
    top: var(--market-header-h);
    left: 0;
    /* 높이를 직접 준다 — DynamicSidebar 쪽 주석 참고. bottom 으로 맡기면
     * 메뉴가 화면보다 길 때 top 이 무시되고 헤더를 덮는다. */
    height: calc(100vh - var(--market-header-h));
    height: calc(100dvh - var(--market-header-h));
    width: min(280px, 84vw);
    flex-basis: auto;
    max-height: none;
    z-index: 1100;
    transform: translateX(${(props) => (props.open ? '0' : '-100%')});
    transition: transform 0.25s ease;
    visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SidebarHeader = styled.div`
  padding: 0 22px 22px;
  border-bottom: 1px solid #243244;
  margin-bottom: 22px;
`;

const SidebarTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0;
  color: #fff;
  font-size: 18px;
  font-weight: 900;
`;

const SidebarSubtitle = styled.p`
  margin: 6px 0 0;
  color: #94a3b8;
  font-size: 12px;
`;

const NavSection = styled.div`
  margin-bottom: 18px;
`;

const SectionTitle = styled.div`
  padding: 0 22px 10px;
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.6px;
`;

const NavList = styled.div`
  display: grid;
  gap: 3px;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 11px;
  min-height: 42px;
  padding: 0 18px 0 22px;
  border-right: 3px solid ${(props) => (props.$active ? "#60a5fa" : "transparent")};
  background: ${(props) => (props.$active ? "#1d4ed8" : "transparent")};
  color: ${(props) => (props.$active ? "#fff" : "#cbd5e1")};
  text-decoration: none;
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? "900" : "700")};

  &:hover {
    background: ${(props) => (props.$active ? "#1d4ed8" : "#1f2937")};
    color: #fff;
  }
`;

const NavLabel = styled.span`
  flex: 1;
`;

const Badge = styled.span`
  min-width: 19px;
  height: 19px;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  line-height: 19px;
  text-align: center;
`;

const navigationItems = [
  {
    section: "OVERVIEW",
    items: [{ path: "/admin", label: "대시보드", icon: <LayoutDashboard size={19} />, exact: true }],
  },
  {
    section: "COMMERCE",
    items: [
      { path: "/admin/orders", label: "주문 관리", icon: <ShoppingBag size={19} />, badge: "5" },
      { path: "/admin/products", label: "상품 관리", icon: <Package size={19} /> },
      { path: "/admin/agent-submissions", label: "에이전트 등록 대기열", icon: <Boxes size={19} /> },
      { path: "/admin/inventory", label: "재고 관리", icon: <Boxes size={19} />, badge: "2" },
      { path: "/admin/members", label: "회원 관리", icon: <Users size={19} /> },
      { path: "/admin/coupons", label: "쿠폰 관리", icon: <Gift size={19} /> },
      { path: "/admin/points", label: "포인트 관리", icon: <Coins size={19} /> },
    ],
  },
  {
    section: "GROWTH",
    items: [
      { path: "/admin/analytics", label: "통계 분석", icon: <ChartNoAxesCombined size={19} /> },
      { path: "/admin/marketing", label: "마케팅", icon: <Mail size={19} /> },
      { path: "/admin/notifications", label: "알림 관리", icon: <Bell size={19} />, badge: "3" },
    ],
  },
  {
    section: "SYSTEM",
    items: [
      { path: "/admin/management", label: "운영 설정", icon: <Settings size={19} /> },
      { path: "/admin/database", label: "데이터베이스", icon: <Database size={19} /> },
    ],
  },
];

export default function AdminSideNavigator({ open = false, onClose }) {
  const location = useLocation();

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <AdminSidebar open={open}>
      <SidebarHeader>
        <SidebarTitle>
          <BarChart3 size={20} />
          ANTS Admin
        </SidebarTitle>
        <SidebarSubtitle>쇼핑몰 운영 콘솔</SidebarSubtitle>
      </SidebarHeader>

      {navigationItems.map((section) => (
        <NavSection key={section.section}>
          <SectionTitle>{section.section}</SectionTitle>
          <NavList>
            {section.items.map((item) => (
              <NavItem key={item.path} to={item.path} $active={isActive(item)}>
                {item.icon}
                <NavLabel>{item.label}</NavLabel>
                {item.badge && <Badge>{item.badge}</Badge>}
              </NavItem>
            ))}
          </NavList>
        </NavSection>
      ))}
    </AdminSidebar>
  );
}
